import { defineComponent as _defineComponent } from 'vue'
import { ref } from 'vue'
import { useCreateLocationMutation } from '@/api/account-data/manage-locations'
import { useFormStates } from '@/components/locations/on-the-fly-locations/form-states'
import { useMapView } from '@/components/locations/on-the-fly-locations/map-view'
import { useMinimapView } from '@/components/locations/on-the-fly-locations/minimap-view'
import LocationDTO from '@/models/location'
import VsAlert from '@/components/vision/VsAlert.vue'
import FormLayout from '@/components/common/FormLayout.vue'
import VsForm from '@/components/vision/VsForm.vue'
import OnTheFlyLocationRequiredFields from '@/components/locations/on-the-fly-locations/OnTheFlyLocationRequiredFields.vue'
import OnTheFlyLocationOptionalFieldsMenu from '@/components/locations/on-the-fly-locations/OnTheFlyLocationOptionalFieldsMenu.vue'
import OnTheFlyLocationCoordinatesField from '@/components/locations/on-the-fly-locations/OnTheFlyLocationCoordinatesField.vue'
import OnTheFlyLocationOptionalFields from '@/components/locations/on-the-fly-locations/OnTheFlyLocationOptionalFields.vue'
import NewTag from '@/models/new_tag'
import { useOverwriteLocationTags } from '@/api/locations'

export default /*#__PURE__*/_defineComponent({
  __name: 'OnTheFlyLocationForm',
  props: {
    defaultParentLocId: null
  },
  emits: ['close'],
  setup(__props: any, { emit }) {

const props = __props as {
  defaultParentLocId?: number | null
}




const form = ref()
const coordinatesField = ref()
const errorMessage = ref<string>()

const { isLoading: isCreatingLocation, mutateAsync: createLocation } =
  useCreateLocationMutation()
const {
  mutateAsync: overwriteLocationTags,
  isLoading: isOverwritingLocationTags,
} = useOverwriteLocationTags()
const {
  formData,
  mapCenterPoint,
  isInMapView,
  showCoordinatesField,
  removeCoordinates,
  showParentLocationField,
  removeParentLocation,
  showDescriptionField,
  removeDescription,
  showStatusField,
  removeStatus,
  showTagsField,
  removeTags,
} = useFormStates(() => props.defaultParentLocId)

const {
  map,
  insertLocationIntoMapModifiedLayer,
  syncLocationDetailsToMapMarker,
} = useMapView(formData, isInMapView)

const { onMinimapMoved } = useMinimapView(formData, isInMapView, mapCenterPoint)

// Methods
const dismiss = () => {
  emit('close', null)
}
const submit = async () => {
  if (!form.value?.isValid()) return

  if (
    formData.ownerId &&
    formData.location &&
    formData.locationTypeId != null &&
    (formData.accessBusinessUnitIds || []).length > 0
  ) {
    const response = await createLocation({
      ...(formData as Partial<LocationDTO>),
    })
    if (response.data) {
      if (formData.tags && formData.tags.length > 0) {
        await overwriteLocationTags({
          locationId: response.data,
          tags: formData.tags,
        })
      }
      if (isInMapView.value) {
        await insertLocationIntoMapModifiedLayer(response.data)
      }
      emit('close', response.data)
    }
  } else {
    errorMessage.value = `Please fill all required fields`
  }
}
const handleFormDataChanged = (
  e: string | number | number[] | NewTag[] | null | undefined,
  fieldChanged:
    | 'name'
    | 'location-type'
    | 'owner'
    | 'parent-location'
    | 'description'
    | 'color'
    | 'private'
    | 'tags'
) => {
  if (fieldChanged === 'name') {
    formData.location = e?.toString() ?? null
  } else if (fieldChanged === 'location-type') {
    if (e === null || e === undefined) {
      formData.locationTypeId = null
    } else {
      formData.locationTypeId =
        typeof e === 'number' ? e : parseInt(e as string)
    }
  } else if (fieldChanged === 'owner') {
    formData.ownerId = e?.toString() ?? null
  } else if (fieldChanged === 'parent-location') {
    if (e === null || e === undefined) {
      formData.parentId = null
    } else {
      formData.parentId = typeof e === 'number' ? e : parseInt(e as string)
    }
  } else if (fieldChanged === 'description') {
    formData.description = e?.toString() ?? null
  } else if (fieldChanged === 'color') {
    if (e === null || e === undefined) {
      formData.colorId = null
    } else {
      formData.colorId = typeof e === 'number' ? e : parseInt(e as string)
    }
  } else if (fieldChanged === 'private') {
    formData.private = !!e
  } else if (fieldChanged === 'tags') {
    formData.tags = e as NewTag[]
  }
  if (isInMapView.value) {
    syncLocationDetailsToMapMarker()
  }
}
const onLatitudeInput = (e: string) => {
  const parsedData = parseFloat(parseFloat(e).toFixed(8))
  if (!isNaN(parsedData)) {
    formData.latitude = parsedData
    if (!isInMapView.value) {
      coordinatesField.value.jumpMinimapTo({
        latitude: formData.latitude ?? 0,
        longitude: formData.longitude ?? 0,
      })
    }
  }
}
const onLongitudeInput = (e: string) => {
  const parsedData = parseFloat(parseFloat(e).toFixed(8))
  if (!isNaN(parsedData)) {
    formData.longitude = parsedData
    if (!isInMapView.value) {
      coordinatesField.value.jumpMinimapTo({
        latitude: formData.latitude ?? 0,
        longitude: formData.longitude ?? 0,
      })
    }
  }
}

const toggleCoordinatesFieldOn = () => {
  if (isInMapView.value && map.value) {
    const { lat, lng } = map.value.getCenter()
    formData.latitude = lat
    formData.longitude = lng
  } else {
    formData.latitude = mapCenterPoint.value?.latitude ?? 0
    formData.longitude = mapCenterPoint.value?.longitude ?? 0
  }
  showCoordinatesField.value = true
}

return { __sfc: true,props, emit, form, coordinatesField, errorMessage, isCreatingLocation, createLocation, overwriteLocationTags, isOverwritingLocationTags, formData, mapCenterPoint, isInMapView, showCoordinatesField, removeCoordinates, showParentLocationField, removeParentLocation, showDescriptionField, removeDescription, showStatusField, removeStatus, showTagsField, removeTags, map, insertLocationIntoMapModifiedLayer, syncLocationDetailsToMapMarker, onMinimapMoved, dismiss, submit, handleFormDataChanged, onLatitudeInput, onLongitudeInput, toggleCoordinatesFieldOn, VsAlert, FormLayout, VsForm, OnTheFlyLocationRequiredFields, OnTheFlyLocationOptionalFieldsMenu, OnTheFlyLocationCoordinatesField, OnTheFlyLocationOptionalFields }
}

})